import { Box, Grid2, IconButton, Typography } from '@mui/material';
import { AutocompleteField, InputField } from 'components/form';
import { getSubmissionDeclineReasonsOptions } from 'modules/creditSubmission/lib';
import { DeclineReasonOption } from 'modules/creditSubmission/types';
import makeStyles from '@mui/styles/makeStyles';
import React, { useMemo } from 'react';
import { FieldValues, Path, useFormContext } from 'react-hook-form';
import EditIcon from '@mui/icons-material/Edit';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { CreditSubmissionDeclineReason } from '@trnsact/trnsact-shared-types';
import { ApolloQueryResult } from '@apollo/client';

interface DeclineFormProps<T extends FieldValues> {
  isEditable?: boolean;
  creditSubmissionDeclineReasons: CreditSubmissionDeclineReason[];
  refetchCreditSubmissionDeclineReasons?: () => Promise<ApolloQueryResult<any>>;
}

export const DeclineForm = <T extends FieldValues>({
  isEditable,
  creditSubmissionDeclineReasons,
  refetchCreditSubmissionDeclineReasons,
}: DeclineFormProps<T>) => {
  const classes = useStyles();

  const { handleOpen: handleOpenAddEditDeclineReasonDialog } = useModal(ModalsKeys.AddEditDeclineReasonDialog);

  const { control } = useFormContext<T>();

  const declineReasonOptions = useMemo(
    () => getSubmissionDeclineReasonsOptions(creditSubmissionDeclineReasons),
    [creditSubmissionDeclineReasons]
  );

  const handleEditIconClick = () => {
    handleOpenAddEditDeclineReasonDialog({
      creditSubmissionDeclineReasons,
      refetchCreditSubmissionDeclineReasons: refetchCreditSubmissionDeclineReasons!,
    });
  };

  return (
    <Grid2 container spacing={2}>
      <Grid2 size={6} className={classes.reasonForDecline}>
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography variant="h6" component="p">
            Reason for Decline
          </Typography>
          {isEditable && (
            <IconButton size="large" onClick={handleEditIconClick}>
              <EditIcon color="primary" />
            </IconButton>
          )}
        </Box>
        <AutocompleteField<DeclineReasonOption, true, T>
          control={control}
          name={'creditSubmissionDeclineReason' as Path<T>}
          options={declineReasonOptions.sort((a, b) => -b.category.localeCompare(a.category))}
          groupBy={option => option.category}
          getOptionLabel={option => option.label}
          textFieldProps={{ label: 'Select Reason', required: true }}
          isOptionEqualToValue={(option, value) => value && option.value === value?.value}
          disableClearable={true as any}
        />
      </Grid2>

      <Grid2 size={6}>
        <InputField
          control={control}
          name={'notes' as Path<T>}
          label="Notes"
          rules={{ required: true }}
          inputProps={{ multiline: true, fullWidth: true, rows: 7, variant: 'outlined', required: true }}
        />
      </Grid2>
    </Grid2>
  );
};

const useStyles = makeStyles({
  reasonForDecline: {
    backgroundColor: '#F5F5F5',
    padding: '16px',
  },
});
